<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute, useRouter } from 'vue-router';
import { useAlert } from 'dashboard/composables';
import LaisCard from 'dashboard/components-next/LaisCard.vue';
import LaisHeader from 'dashboard/components-next/LaisHeader.vue';
import LaisPrimaryButton from 'dashboard/components-next/LaisPrimaryButton.vue';
import RealEstateBrokerInvestmentDesktopTable from '../components/RealEstateBrokerInvestmentDesktopTable.vue';
import RealEstateBrokerInvestmentFilters from '../components/RealEstateBrokerInvestmentFilters.vue';
import RealEstateBrokerInvestmentMobileList from '../components/RealEstateBrokerInvestmentMobileList.vue';
import RealEstateBrokerInvestmentPagination from '../components/RealEstateBrokerInvestmentPagination.vue';
import RealEstateBrokerInvestmentSummary from '../components/RealEstateBrokerInvestmentSummary.vue';
import RealEstateBrokerInvestmentTabs from '../components/RealEstateBrokerInvestmentTabs.vue';
import RealEstateBrokerDesktopNavigation from '../components/RealEstateBrokerDesktopNavigation.vue';
import RealEstateBrokerMobileNavigation from '../components/RealEstateBrokerMobileNavigation.vue';
import { createRealEstateBrokerInvestmentPresenter } from '../composables/realEstateBrokerInvestmentPresenter';
import { useRealEstateBrokerInvestments } from '../composables/useRealEstateBrokerInvestments';

const { t } = useI18n();
const route = useRoute();
const router = useRouter();
const investments = useRealEstateBrokerInvestments({ notify: useAlert, t });
const { temperatureLabel } = createRealEstateBrokerInvestmentPresenter(t);
const isPortfolio = computed(() => !investments.temperature.value);
const tabItems = computed(() => [
  {
    value: '',
    label: t('REAL_ESTATE_BROKER_INVESTMENT.TABS.VGV'),
    count: investments.summary.value.quantidade_total || 0,
  },
  ...(investments.summary.value.temperaturas || []).map(item => ({
    value: item.slug,
    label: temperatureLabel(item.slug),
    count: item.quantidade || 0,
  })),
]);
const refreshLabel = computed(() => {
  if (investments.minutesSinceRefresh.value === null) return '';

  return t('REAL_ESTATE_BROKER_INVESTMENT.REFRESHED_AT', {
    minutes: investments.minutesSinceRefresh.value,
  });
});
const tabLabel = item =>
  t('REAL_ESTATE_BROKER_INVESTMENT.TABS.LABEL', {
    label: item.label,
    count: item.count,
  });
const openProperty = id =>
  router.push({
    name: 'operacao_imobiliaria_corretor_investimento',
    params: { accountId: route.params.accountId, investmentId: id },
  });
</script>

<template>
  <div class="min-h-full bg-lais-bg-soft">
    <LaisHeader
      :title="t('REAL_ESTATE_BROKER_INVESTMENT.TITLE')"
      :subtitle="t('REAL_ESTATE_BROKER_INVESTMENT.SUBTITLE')"
      :breadcrumb="[
        { label: t('REAL_ESTATE_BROKER_DASHBOARD.TITLE') },
        { label: t('REAL_ESTATE_BROKER_INVESTMENT.TITLE') },
      ]"
    >
      <template #actions>
        <p v-if="refreshLabel" class="text-xs text-lais-text-muted">
          {{ refreshLabel }}
        </p>
        <LaisPrimaryButton
          icon="i-lucide-refresh-cw"
          variant="ghost"
          :is-loading="investments.loading.value"
          @click="investments.load"
        >
          {{ t('REAL_ESTATE_BROKER_INVESTMENT.ACTIONS.REFRESH') }}
        </LaisPrimaryButton>
      </template>
    </LaisHeader>
    <div class="mx-auto flex w-full max-w-[1900px]">
      <RealEstateBrokerDesktopNavigation
        :investment-summary="investments.summary.value"
        :selected-investment="investments.temperature.value"
        @select-investment="investments.selectTemperature"
      />
      <main
        class="flex min-w-0 flex-1 flex-col gap-5 px-4 pb-24 sm:px-6 lg:px-10 md:pb-8"
      >
        <RealEstateBrokerInvestmentTabs
          :active="investments.temperature.value"
          :items="tabItems"
          :label-for="tabLabel"
          @select="investments.selectTemperature"
        />
        <p
          v-if="investments.error.value"
          class="rounded-lais border border-red-200 bg-red-50 p-3 text-sm text-red-700"
        >
          {{ investments.error.value }}
        </p>
        <template v-if="isPortfolio">
          <LaisCard density="tight">
            <p class="flex items-center gap-2 p-4 text-sm text-lais-text-body">
              <span class="i-lucide-star size-4 text-amber-500" />
              {{ t('REAL_ESTATE_BROKER_INVESTMENT.PORTFOLIO_HELPER') }}
            </p>
          </LaisCard>
          <RealEstateBrokerInvestmentSummary
            :active-temperature="investments.temperature.value"
            :loading="investments.loading.value"
            :summary="investments.summary.value"
            @select="investments.selectTemperature"
          />
        </template>
        <template v-else>
          <RealEstateBrokerInvestmentFilters
            v-model:search="investments.search.value"
            v-model:sort="investments.sort.value"
            :loading="investments.loading.value"
          />
          <RealEstateBrokerInvestmentDesktopTable
            :loading="investments.loading.value"
            :rows="investments.rows.value"
            @open="openProperty"
          />
          <RealEstateBrokerInvestmentMobileList
            :loading="investments.loading.value"
            :rows="investments.rows.value"
            @open="openProperty"
          />
          <RealEstateBrokerInvestmentPagination
            v-if="investments.totalPages.value > 1"
            :page="investments.page.value"
            :total-pages="investments.totalPages.value"
            @change="investments.changePage"
          />
        </template>
      </main>
    </div>
    <RealEstateBrokerMobileNavigation />
  </div>
</template>
